
const libFableServiceProviderBase = require('fable-serviceproviderbase');

/**
 * Browser-side Fable service that renders a Headlight Observation into HTML.
 *
 * This is the centralized observation renderer — the sibling of
 * web-headlight-document-render (documents contain observations; the two are
 * used in concert).  It replaces the scattered, ad-hoc observation rendering
 * that had grown up across the platform (the config-app Observation workspace's
 * bespoke `_renderDatum` walker, the legacy AngularJS/Mustache transcoder's
 * one-file-per-type templates, MediaCardBuilder, the Explorer map panels, …),
 * each of which re-derived its own field selection and its own data source.
 *
 * Two ideas make it "intelligent about built-in AND custom schemas":
 *
 *   1.  It never branches on built-in-vs-custom.  The API's manifest resolver
 *       already merges a customer's custom ObservationManifest over the global
 *       (IDCustomer=0) default, so a single lookup returns whichever wins.  This
 *       service just asks `GET ObservationSpecification/{type}/{project}` for the
 *       resolved schema (slots → ArtifactType.Schemata.properties) and drives
 *       layout off it.  Custom types "just work."
 *
 *   2.  It is schema-DRIVEN, not template-per-type.  One data pass builds a
 *       normalized model from the observation's assembled `.Details`
 *       (`{ <SlotName>: [ { IDArtifact, ArtifactType, Version, Datum:{...} }, ... ] }`),
 *       iterating EVERY artifact in EVERY slot (the old code took only `[0]`,
 *       which is why spreadsheet observations — one Artifact per row — showed a
 *       single row or nothing).  A slot whose CountMaximum>1 (or that holds
 *       multiple artifacts) renders as a header+rows table; a single record as a
 *       field card; a Data_String as prose; media as thumbnails/links.
 *
 * The same model feeds many named FORMATS (like the transcoder's one-template-
 * many-sizes model, but the "size" changes how much of the model is shown, not
 * just CSS):
 *
 *   full       Workspace Data body — hero media + meta + description + all
 *              captured-data slots (tables/cards/prose/media) + labels.
 *   card       ~square grid cell — square media (or type glyph) + name + type +
 *              a few headline datums.  Supersedes MediaCardBuilder / transcode Preview.
 *   strip      Thin rail item for the side of a map — thumbnail + name + type dot +
 *              date + 1-2 headline datums.
 *   tooltip    Data-laden hover popup — square image + name/type/date/author + top datums.
 *   thumbnail  128px — image or typed glyph + initials + date.
 *   marker     One-line map label — name · type.
 *
 * Every format degrades gracefully: handed a full Observation (with `.Details`)
 * it shows captured data; handed a thin Solr/geo doc (Name / RecordSubType /
 * DateOfRecord / IDAuthor / IDArtifact, no Details) it shows a metadata-only
 * compact shape and a typed glyph rather than a broken image.
 *
 * Non-image observations always render as HTML — never a broken `<img>`.
 *
 * Consumed like its sibling:
 *   pict.addServiceType('HeadlightObservationRender', require('.../Web-Headlight-Observation-Render.js'));
 *   pict.instantiateServiceProviderIfNotExists('HeadlightObservationRender');
 *   const html = pict.HeadlightObservationRender.renderObservationHTML(observation, { format: 'full' });
 */
class WebHeadlightObservationRenderService extends libFableServiceProviderBase
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		this.serviceType = 'HeadlightObservationRender';

		// options.RestClient overrides discovery — useful for tests + standalone use.
		this._OverrideRestClient = (this.options && this.options.RestClient) ? this.options.RestClient : null;

		// ObservationSpecification cache, keyed by `${ObservationType}::${IDProject||0}`.
		this._SpecificationCache = {};

		// Guard so injectStyles() only writes the <style> block once per document.
		this._StylesInjected = false;

		// Artifact types that carry media bytes rather than structured Datum fields.
		this._MediaArtifactTypes = { Image: 'image', Video: 'video', Audio: 'audio', File: 'file', Data_File: 'file' };
	}

	/**
	 * Resolve the HeadlightRestClient instance regardless of where it lives.
	 * Discovery order:
	 *   1. options.RestClient (constructor override)
	 *   2. fable.HeadlightRestClient
	 *   3. fable.pict.HeadlightRestClient
	 */
	get _restClient()
	{
		if (this._OverrideRestClient) { return this._OverrideRestClient; }
		if (this.fable && this.fable.HeadlightRestClient) { return this.fable.HeadlightRestClient; }
		if (this.fable && this.fable.pict && this.fable.pict.HeadlightRestClient) { return this.fable.pict.HeadlightRestClient; }
		return null;
	}

	setRestClient(pClient)
	{
		this._OverrideRestClient = pClient || null;
	}

	// ---- URL helpers ----------------------------------------------------

	/** Same-origin API URL for a path, via the rest client when available. */
	_formatUrl(pPath)
	{
		const tmpClient = this._restClient;
		if (tmpClient && (typeof tmpClient.formatUrl === 'function'))
		{
			try { return tmpClient.formatUrl(pPath); } catch (pError) { /* fall through */ }
		}
		return `/1.0/${pPath}`;
	}

	/**
	 * The rendered/composited observation image (annotations burned in).  Sizes:
	 * Thumbnail (128, reliably generated), Standard, Preview (larger, may lag).
	 * Compact formats use Thumbnail because larger sizes are generated lazily.
	 */
	observationImageURL(pIDObservation, pSize, pVersion)
	{
		if (!pIDObservation) { return null; }
		const tmpSize = pSize || 'Thumbnail';
		const tmpBase = `Observation/${pIDObservation}/Image/${tmpSize}`;
		return this._formatUrl((pVersion != null && pVersion !== '') ? `${tmpBase}/V/${pVersion}` : tmpBase);
	}

	/** Raw media stream for one artifact version. */
	artifactMediaURL(pIDArtifact, pVersion)
	{
		if (!pIDArtifact) { return null; }
		return this._formatUrl(`Artifact/Media/${Number(pIDArtifact)}/${Number(pVersion) || 1}`);
	}

	// ---- Specification (schema) loading ---------------------------------

	/**
	 * Load and cache the resolved schema for an ObservationType (built-in or
	 * custom — the API resolver merges them).  The schema drives column order,
	 * labels, and visibility.  Rendering still works WITHOUT it (columns fall
	 * back to the union of Datum keys), so this is an enhancement, not a
	 * requirement.
	 *
	 * @param {string} pObservationType
	 * @param {number|string} pIDProject   Project context for custom-type resolution.
	 * @param {(pError: Error|null, pSpecification?: object) => void} fCallback
	 *        pSpecification = { slots:[{name,idArtifactType,countMax,required}],
	 *                           schemasByID:{<id>:{name,columns:[...]}},
	 *                           schemasByName:{<name>:{...}} }
	 */
	loadSpecification(pObservationType, pIDProject, fCallback)
	{
		if (typeof pIDProject === 'function') { fCallback = pIDProject; pIDProject = null; }
		const tmpCallback = (typeof fCallback === 'function') ? fCallback : (() => {});

		if (!pObservationType) { return tmpCallback(new Error('HeadlightObservationRender: ObservationType is required.')); }

		const tmpKey = `${pObservationType}::${pIDProject || 0}`;
		if (this._SpecificationCache[tmpKey]) { return tmpCallback(null, this._SpecificationCache[tmpKey]); }

		const tmpClient = this._restClient;
		if (!tmpClient) { return tmpCallback(new Error('HeadlightObservationRender: HeadlightRestClient is not available.')); }

		const tmpURL = `ObservationSpecification/${encodeURIComponent(pObservationType)}${(pIDProject != null && pIDProject !== '') ? ('/' + pIDProject) : ''}`;
		tmpClient.getJSON(tmpURL, (pError, pBody) =>
		{
			if (pError) { return tmpCallback(pError); }
			if (!pBody || pBody.Error) { return tmpCallback(new Error((pBody && pBody.Error) ? String(pBody.Error) : 'ObservationSpecification returned no body.')); }
			let tmpParsed;
			try { tmpParsed = this._parseSpecification(pBody, pObservationType); }
			catch (pParseError) { return tmpCallback(pParseError); }
			this._SpecificationCache[tmpKey] = tmpParsed;
			return tmpCallback(null, tmpParsed);
		});
	}

	/**
	 * Turn a raw ObservationSpecification `{ Manifest:{<type>:[slots]}, ArtifactTypes:{<id>:{Name,Schemata}} }`
	 * into `{ slots, schemasByID, schemasByName }` with parsed, sorted columns.
	 */
	_parseSpecification(pSpecification, pObservationType)
	{
		const tmpArtifactTypes = pSpecification.ArtifactTypes || {};
		const tmpSchemasByID = {};
		const tmpSchemasByName = {};
		Object.keys(tmpArtifactTypes).forEach((pID) =>
		{
			const tmpAT = tmpArtifactTypes[pID] || {};
			const tmpColumns = this._columnsFromSchemata(tmpAT.Schema || tmpAT.Schemata);
			const tmpEntry = { id: pID, name: tmpAT.Name, columns: tmpColumns };
			tmpSchemasByID[String(pID)] = tmpEntry;
			if (tmpAT.Name) { tmpSchemasByName[tmpAT.Name] = tmpEntry; }
		});

		const tmpManifest = pSpecification.Manifest || {};
		// Prefer the requested type's slot list; else the first manifest key present.
		const tmpTypeKey = (pObservationType && tmpManifest[pObservationType]) ? pObservationType : Object.keys(tmpManifest)[0];
		const tmpRawSlots = (tmpTypeKey && Array.isArray(tmpManifest[tmpTypeKey])) ? tmpManifest[tmpTypeKey] : [];
		const tmpSlots = tmpRawSlots.map((pSlot) => (
			{
				name: pSlot.Name,
				idArtifactType: (pSlot.IDArtifactType != null) ? String(pSlot.IDArtifactType) : null,
				countMax: Number(pSlot.CountMaximum) || 0,
				required: !!pSlot.Required
			}));

		return { slots: tmpSlots, schemasByID: tmpSchemasByID, schemasByName: tmpSchemasByName };
	}

	/**
	 * Parse an ArtifactType.Schemata (JSON-Schema, string or object) into an
	 * ordered column list: `[ { key, label, type, ordinal } ]`, sorted by
	 * `ordinal`, honouring `displayName`, dropping `visible === false`.
	 */
	_columnsFromSchemata(pSchemata)
	{
		let tmpSchema = pSchemata;
		if (typeof tmpSchema === 'string')
		{
			try { tmpSchema = JSON.parse(tmpSchema); } catch (pError) { return []; }
		}
		if (!tmpSchema || (typeof tmpSchema !== 'object') || !tmpSchema.properties) { return []; }
		const tmpProps = tmpSchema.properties;
		return Object.keys(tmpProps)
			.map((pKey) =>
			{
				const tmpProp = tmpProps[pKey] || {};
				return {
					key: pKey,
					label: tmpProp.displayName || this._prettyKey(pKey),
					type: tmpProp.type || 'string',
					ordinal: (tmpProp.ordinal != null) ? Number(tmpProp.ordinal) : 9999,
					visible: (tmpProp.visible !== false)
				};
			})
			.filter((pColumn) => pColumn.visible)
			.sort((pA, pB) => (pA.ordinal - pB.ordinal) || String(pA.key).localeCompare(String(pB.key)));
	}

	// ---- Public render entry points -------------------------------------

	/**
	 * Async render: ensures the schema is loaded (for schema-driven columns),
	 * then renders.  Pass `options.specification` (from a prior loadSpecification)
	 * or `options.autoLoadSpec: false` to skip the fetch and render immediately
	 * with key-derived columns.
	 *
	 * @param {object} pObservation   A hydrated Observation (with `.Details`) or a thin doc.
	 * @param {object} [pOptions]     { format, specification, autoLoadSpec, creatorName }
	 * @param {(pError: Error|null, pHTML?: string) => void} fCallback
	 */
	renderObservation(pObservation, pOptions, fCallback)
	{
		if (typeof pOptions === 'function') { fCallback = pOptions; pOptions = null; }
		const tmpOptions = pOptions || {};
		const tmpCallback = (typeof fCallback === 'function') ? fCallback : (() => {});

		if (!pObservation) { return tmpCallback(new Error('HeadlightObservationRender: an observation is required.')); }

		const tmpType = pObservation.ObservationType;
		const tmpProject = pObservation.IDProject;

		if (tmpOptions.specification || (tmpOptions.autoLoadSpec === false) || !tmpType || !this._restClient)
		{
			try { return tmpCallback(null, this.renderObservationHTML(pObservation, tmpOptions)); }
			catch (pError) { return tmpCallback(pError); }
		}

		this.loadSpecification(tmpType, tmpProject, (pError, pSpecification) =>
		{
			// A spec failure is non-fatal — render with key-derived columns.
			const tmpMerged = Object.assign({}, tmpOptions, { specification: pError ? null : pSpecification });
			try { return tmpCallback(null, this.renderObservationHTML(pObservation, tmpMerged)); }
			catch (pRenderError) { return tmpCallback(pRenderError); }
		});
	}

	/**
	 * Synchronous render.  Builds the normalized model then dispatches by format.
	 * Auto-injects the stylesheet (once) when a document is available.
	 *
	 * @param {object} pObservation
	 * @param {object} [pOptions]   { format='full', specification, creatorName, injectStyles=true, typeLabel, showArtifactType=true }
	 *        typeLabel(ObservationType) → the type as shown (default: the stored ObservationType);
	 *        showArtifactType=false drops the stored ArtifactType from each slot's badge.
	 * @returns {string} HTML fragment.
	 */
	renderObservationHTML(pObservation, pOptions)
	{
		const tmpOptions = pOptions || {};
		if (tmpOptions.injectStyles !== false) { this.injectStyles(); }

		const tmpModel = this.buildObservationModel(pObservation, tmpOptions);
		const tmpFormat = tmpOptions.format || 'full';
		switch (tmpFormat)
		{
			case 'data':      return this._renderData(tmpModel);
			case 'card':      return this._renderCard(tmpModel);
			case 'strip':     return this._renderStrip(tmpModel);
			case 'tooltip':   return this._renderTooltip(tmpModel);
			case 'thumbnail': return this._renderThumbnail(tmpModel);
			case 'marker':    return this._renderMarker(tmpModel);
			case 'full':
			default:          return this._renderFull(tmpModel);
		}
	}

	// ---- Model building -------------------------------------------------

	/**
	 * Normalize an observation (full or thin) into the render model consumed by
	 * every format.  This is the single data pass; formats only choose how much
	 * of it to show.
	 */
	buildObservationModel(pObservation, pOptions)
	{
		const tmpOptions = pOptions || {};
		const tmpObs = pObservation || {};
		const tmpSpec = tmpOptions.specification || null;

		const tmpID = tmpObs.IDObservation || tmpObs.IDRecord || tmpObs.ID || null;
		const tmpType = tmpObs.ObservationType || tmpObs.RecordSubType || tmpObs.Type || '';
		const tmpTypeLabel = (typeof tmpOptions.typeLabel === 'function') ? (tmpOptions.typeLabel(tmpType) || tmpType) : tmpType;
		const tmpName = tmpObs.Name || tmpObs.ObservationName || tmpObs.SearchText || (tmpID ? `Observation #${tmpID}` : 'Observation');
		const tmpDescription = tmpObs.Description || '';
		const tmpStatus = tmpObs.Status || '';
		const tmpWhen = tmpObs.DesignatedTime || tmpObs.DateOfRecord || tmpObs.CreateDate || '';
		const tmpCreator = tmpOptions.creatorName || tmpObs.CreatingUserName || tmpObs.AuthorName || tmpObs.Author || '';
		const tmpAuthorID = tmpObs.CreatingIDUser || tmpObs.IDAuthor || null;
		const tmpVersion = tmpObs.Version;

		const tmpDetails = (tmpObs.Details && (typeof tmpObs.Details === 'object')) ? tmpObs.Details : {};
		const tmpHasDetails = Object.keys(tmpDetails).length > 0;

		const tmpSlots = [];
		let tmpHeroArtifact = null;
		Object.keys(tmpDetails).forEach((pSlotName) =>
		{
			const tmpArray = Array.isArray(tmpDetails[pSlotName]) ? tmpDetails[pSlotName] : [];
			if (!tmpArray.length) { return; }
			const tmpSlotModel = this._buildSlotModel(pSlotName, tmpArray, tmpSpec);
			tmpSlotModel.showArtifactType = (tmpOptions.showArtifactType !== false);
			// The first image becomes the hero; annotation overlays are not shown as data.
			if (tmpSlotModel.kind === 'media' && tmpSlotModel.mediaKind === 'image' && !tmpHeroArtifact)
			{
				tmpHeroArtifact = tmpSlotModel.items[0];
				tmpSlotModel.isHeroSource = true;
			}
			tmpSlots.push(tmpSlotModel);
		});

		// Whether we can show a composited observation image.
		const tmpHasImage = !!tmpHeroArtifact
			|| (!tmpHasDetails && (!!tmpObs.IDArtifact || tmpObs.HasImage === true || /image|photo/i.test(tmpType)));

		const tmpLabels = [];
		const tmpRawLabels = (tmpObs.Labels && (typeof tmpObs.Labels === 'object')) ? tmpObs.Labels : {};
		Object.keys(tmpRawLabels).forEach((pType) =>
		{
			const tmpValues = (Array.isArray(tmpRawLabels[pType]) ? tmpRawLabels[pType] : [])
				.map((pTag) => (pTag && (pTag.Tag != null ? pTag.Tag : pTag)))
				.filter((pValue) => pValue != null && pValue !== '');
			if (tmpValues.length) { tmpLabels.push({ type: pType, label: this._prettyKey(pType), values: tmpValues }); }
		});

		return {
			id: tmpID,
			type: tmpType,
			typeLabel: tmpTypeLabel,
			name: tmpName,
			description: tmpDescription,
			status: tmpStatus,
			when: tmpWhen,
			creatorName: tmpCreator,
			authorID: tmpAuthorID,
			version: tmpVersion,
			hasDetails: tmpHasDetails,
			hasImage: tmpHasImage,
			imageThumbURL: (tmpID ? this.observationImageURL(tmpID, 'Thumbnail') : null),
			imagePreviewURL: (tmpID ? this.observationImageURL(tmpID, 'Preview') : null),
			slots: tmpSlots,
			labels: tmpLabels
		};
	}

	/** Build one slot's model: classify (media / prose / table / fields) + columns/rows. */
	_buildSlotModel(pSlotName, pArray, pSpecification)
	{
		const tmpFirst = pArray[0] || {};
		const tmpArtifactType = tmpFirst.ArtifactType || pSlotName;
		const tmpMediaKind = this._MediaArtifactTypes[tmpArtifactType] || ((pSlotName === 'Image') ? 'image' : null);

		const tmpBase = { name: pSlotName, prettyName: this._prettyKey(pSlotName), artifactType: tmpArtifactType, count: pArray.length };

		// Annotation slots are overlay geometry, not user-facing data.
		if (pSlotName === 'Annotation') { return Object.assign(tmpBase, { kind: 'skip' }); }

		// Media slots: build item list (image tiles / file links / a/v).
		if (tmpMediaKind)
		{
			const tmpItems = pArray.map((pArtifact) =>
			{
				const tmpDatum = pArtifact.Datum || {};
				return {
					idArtifact: pArtifact.IDArtifact,
					version: (pArtifact.Version != null) ? pArtifact.Version : 1,
					mediaKind: tmpMediaKind,
					fileName: tmpDatum.FileName || pArtifact.Name || tmpArtifactType,
					mimeType: tmpDatum.FileMimeType || '',
					url: this.artifactMediaURL(pArtifact.IDArtifact, (pArtifact.Version != null) ? pArtifact.Version : 1)
				};
			});
			return Object.assign(tmpBase, { kind: 'media', mediaKind: tmpMediaKind, items: tmpItems });
		}

		// Prose slots: a Data_String / MultiLine text capture (journal narrative).
		const tmpLooksProse = (tmpArtifactType === 'Data_String')
			|| pArray.every((pArtifact) => { const d = pArtifact.Datum || {}; return (d.MultiLine === true) || (('Value' in d) && (Object.keys(d).filter((k) => k !== 'MultiLine').length === 1)); });
		if (tmpLooksProse)
		{
			const tmpTexts = pArray
				.map((pArtifact) => { const d = pArtifact.Datum || {}; return (d.Value != null) ? String(d.Value) : ''; })
				.filter((pText) => pText !== '');
			if (tmpTexts.length) { return Object.assign(tmpBase, { kind: 'prose', texts: tmpTexts }); }
		}

		// Structured slots: derive columns (schema-driven, else union of keys).
		const tmpColumns = this._columnsForSlot(pSlotName, pArray, pSpecification);
		const tmpRows = pArray.map((pArtifact) => pArtifact.Datum || {});
		const tmpSlotSpec = this._findSlotSpecification(pSpecification, pSlotName);
		const tmpIsTable = (pArray.length > 1) || (!!tmpSlotSpec && tmpSlotSpec.countMax > 1);
		return Object.assign(tmpBase, { kind: tmpIsTable ? 'table' : 'fields', columns: tmpColumns, rows: tmpRows });
	}

	/**
	 * The spec slot named `pSlotName`, or null.  A plain loop rather than Array.prototype.find: some host pages
	 * (legacy AngularJS apps) carry a collections shim that redefines `find` to take a value, not a predicate.
	 */
	_findSlotSpecification(pSpecification, pSlotName)
	{
		const tmpSlots = (pSpecification && Array.isArray(pSpecification.slots)) ? pSpecification.slots : [];
		for (let i = 0; i < tmpSlots.length; i++)
		{
			if (tmpSlots[i] && (tmpSlots[i].name === pSlotName))
			{
				return tmpSlots[i];
			}
		}
		return null;
	}

	/**
	 * Columns for a structured slot.  Schema-driven when the spec has the slot's
	 * ArtifactType (ordered, labelled, visibility-filtered); otherwise the union
	 * of non-noise Datum keys across all rows, in first-seen order.
	 */
	_columnsForSlot(pSlotName, pArray, pSpecification)
	{
		if (pSpecification)
		{
			const tmpSlot = this._findSlotSpecification(pSpecification, pSlotName);
			if (tmpSlot)
			{
				const tmpSchema = (tmpSlot.idArtifactType && pSpecification.schemasByID[tmpSlot.idArtifactType])
					|| (pArray[0] && pArray[0].ArtifactType && pSpecification.schemasByName[pArray[0].ArtifactType]);
				if (tmpSchema && tmpSchema.columns && tmpSchema.columns.length)
				{
					return tmpSchema.columns.map((pColumn) => ({ key: pColumn.key, label: pColumn.label, type: pColumn.type }));
				}
			}
		}
		// Fallback: union of keys across the rows.
		const tmpSeen = {};
		const tmpColumns = [];
		pArray.forEach((pArtifact) =>
		{
			const tmpDatum = pArtifact.Datum || {};
			Object.keys(tmpDatum).forEach((pKey) =>
			{
				if (tmpSeen[pKey] || this._isNoiseKey(pKey, pArtifact.ArtifactType)) { return; }
				tmpSeen[pKey] = true;
				tmpColumns.push({ key: pKey, label: this._prettyKey(pKey), type: (typeof tmpDatum[pKey] === 'number') ? 'number' : 'string' });
			});
		});
		return tmpColumns;
	}

	// ---- Format renderers ----------------------------------------------

	_renderFull(pModel)
	{
		const tmpHero = pModel.hasImage
			? `<div class="hor-hero"><img class="hor-hero-img" src="${this._esc(pModel.imagePreviewURL)}" alt="${this._esc(pModel.name)}" onerror="this.parentNode.classList.add('hor-broken')"/></div>`
			: '';

		const tmpMeta = `<div class="hor-meta">
			${this._metaField('Name', pModel.name)}
			${this._metaField('Type', pModel.typeLabel)}
			${pModel.status ? this._metaField('Status', pModel.status) : ''}
			${pModel.when ? this._metaField('Observed', this._fmtDate(pModel.when)) : ''}
			${pModel.creatorName ? this._metaField('Captured by', pModel.creatorName) : ''}
			${(pModel.version != null && pModel.version !== '') ? this._metaField('Version', pModel.version) : ''}
		</div>`;

		const tmpDesc = pModel.description
			? `<div class="hor-desc"><div class="hor-meta-label">Description</div><div class="hor-desc-body">${this._esc(pModel.description)}</div></div>`
			: '';

		const tmpDataSlots = pModel.slots
			.filter((pSlot) => pSlot.kind !== 'skip' && !(pSlot.isHeroSource && pSlot.count === 1))
			.map((pSlot) => this._slotCardHTML(pSlot))
			.filter(Boolean)
			.join('');
		const tmpData = tmpDataSlots
			? `<div class="hor-section-h">Captured data</div><div class="hor-slots">${tmpDataSlots}</div>`
			: (pModel.hasDetails ? '' : '<div class="hor-note">No captured data on this observation.</div>');

		return `<div class="hor hor-full">${tmpHero}${tmpMeta}${tmpDesc}${tmpData}${this._labelsHTML(pModel)}</div>`;
	}

	/** Captured-data slots + labels only (no hero / meta / description) — for embedding beside a caller's own chrome. */
	_renderData(pModel)
	{
		const tmpDataSlots = pModel.slots
			.filter((pSlot) => pSlot.kind !== 'skip')
			.map((pSlot) => this._slotCardHTML(pSlot))
			.filter(Boolean)
			.join('');
		const tmpData = tmpDataSlots ? `<div class="hor-slots">${tmpDataSlots}</div>` : '<div class="hor-note">No captured data on this observation.</div>';
		return `<div class="hor hor-data">${tmpData}${this._labelsHTML(pModel)}</div>`;
	}

	_renderCard(pModel)
	{
		const tmpMedia = pModel.hasImage
			? `<div class="hor-card-media"><img src="${this._esc(pModel.imageThumbURL)}" alt="" loading="lazy" onerror="this.parentNode.classList.add('hor-broken');this.parentNode.innerHTML='${this._glyphHTML(pModel.type)}'"/></div>`
			: `<div class="hor-card-media hor-glyph">${this._glyphHTML(pModel.type)}</div>`;
		const tmpHeadline = this._headlineFields(pModel, 3)
			.map((pField) => `<div class="hor-kv"><span class="hor-k">${this._esc(pField.label)}</span><span class="hor-v">${pField.value}</span></div>`)
			.join('');
		return `<div class="hor hor-card">${tmpMedia}
			<div class="hor-card-body">
				<div class="hor-card-title">${this._esc(pModel.name)}</div>
				<div class="hor-card-sub"><span class="hor-type-dot"></span>${this._esc(pModel.typeLabel || 'Observation')}${pModel.when ? ` · <span class="hor-muted">${this._esc(this._fmtDateShort(pModel.when))}</span>` : ''}</div>
				${tmpHeadline ? `<div class="hor-card-fields">${tmpHeadline}</div>` : ''}
			</div>
		</div>`;
	}

	_renderStrip(pModel)
	{
		const tmpThumb = pModel.hasImage
			? `<img class="hor-strip-thumb" src="${this._esc(pModel.imageThumbURL)}" alt="" loading="lazy" onerror="this.outerHTML='<span class=&quot;hor-strip-thumb hor-glyph&quot;>${this._glyphHTML(pModel.type)}</span>'"/>`
			: `<span class="hor-strip-thumb hor-glyph">${this._glyphHTML(pModel.type)}</span>`;
		const tmpHeadline = this._headlineFields(pModel, 2)
			.map((pField) => `<span class="hor-strip-kv"><span class="hor-k">${this._esc(pField.label)}</span> ${pField.value}</span>`)
			.join('');
		return `<div class="hor hor-strip">${tmpThumb}
			<div class="hor-strip-body">
				<div class="hor-strip-title">${this._esc(pModel.name)}</div>
				<div class="hor-strip-sub"><span class="hor-type-dot"></span>${this._esc(pModel.typeLabel || 'Observation')}${pModel.when ? ` · ${this._esc(this._fmtDateShort(pModel.when))}` : ''}</div>
				${tmpHeadline ? `<div class="hor-strip-fields">${tmpHeadline}</div>` : ''}
			</div>
		</div>`;
	}

	_renderTooltip(pModel)
	{
		const tmpMedia = pModel.hasImage
			? `<div class="hor-tip-media"><img src="${this._esc(pModel.imageThumbURL)}" alt="" loading="lazy" onerror="this.parentNode.classList.add('hor-broken')"/></div>`
			: '';
		const tmpHeadline = this._headlineFields(pModel, 4)
			.map((pField) => `<div class="hor-kv"><span class="hor-k">${this._esc(pField.label)}</span><span class="hor-v">${pField.value}</span></div>`)
			.join('');
		return `<div class="hor hor-tooltip">${tmpMedia}
			<div class="hor-tip-body">
				<div class="hor-tip-title">${this._esc(pModel.name)}</div>
				<div class="hor-tip-sub"><span class="hor-type-dot"></span>${this._esc(pModel.typeLabel || 'Observation')}</div>
				<div class="hor-tip-meta">${pModel.when ? this._esc(this._fmtDate(pModel.when)) : ''}${pModel.creatorName ? ` · ${this._esc(pModel.creatorName)}` : ''}</div>
				${tmpHeadline ? `<div class="hor-tip-fields">${tmpHeadline}</div>` : ''}
			</div>
		</div>`;
	}

	_renderThumbnail(pModel)
	{
		const tmpInner = pModel.hasImage
			? `<img src="${this._esc(pModel.imageThumbURL)}" alt="${this._esc(pModel.name)}" loading="lazy" onerror="this.parentNode.classList.add('hor-broken');this.parentNode.innerHTML='${this._glyphHTML(pModel.type)}<span class=&quot;hor-thumb-init&quot;>${this._esc(this._initials(pModel.creatorName))}</span>'"/>`
			: `${this._glyphHTML(pModel.type)}`;
		return `<div class="hor hor-thumbnail" title="${this._esc(pModel.name)}">${tmpInner}<span class="hor-thumb-cap">${this._esc(this._fmtDateShort(pModel.when))}</span></div>`;
	}

	_renderMarker(pModel)
	{
		return `<span class="hor hor-marker"><strong>${this._esc(pModel.name)}</strong>${pModel.typeLabel ? ` · ${this._esc(pModel.typeLabel)}` : ''}</span>`;
	}

	// ---- Slot card renderers (full format) ------------------------------

	_slotCardHTML(pSlot)
	{
		let tmpBody = '';
		let tmpWide = '';
		switch (pSlot.kind)
		{
			case 'media': tmpBody = this._mediaSlotHTML(pSlot); break;
			case 'prose': tmpBody = this._proseSlotHTML(pSlot); tmpWide = ' hor-slot-wide'; break;
			case 'table': tmpBody = this._tableSlotHTML(pSlot); tmpWide = ' hor-slot-wide'; break;
			case 'fields': tmpBody = this._fieldsSlotHTML(pSlot); break;
			default: return '';
		}
		if (!tmpBody) { return ''; }
		const tmpBadgeParts = [];
		if (pSlot.showArtifactType !== false)
		{
			tmpBadgeParts.push(this._esc(pSlot.artifactType));
		}
		if (pSlot.count > 1)
		{
			tmpBadgeParts.push(String(pSlot.count));
		}
		const tmpBadge = tmpBadgeParts.length ? `<span class="hor-badge">${tmpBadgeParts.join(' · ')}</span>` : '';
		return `<div class="hor-slot${tmpWide}"><div class="hor-slot-h"><span>${this._esc(pSlot.prettyName)}</span>${tmpBadge}</div><div class="hor-slot-b">${tmpBody}</div></div>`;
	}

	_tableSlotHTML(pSlot)
	{
		const tmpColumns = pSlot.columns && pSlot.columns.length ? pSlot.columns : this._columnsFromRows(pSlot.rows);
		if (!tmpColumns.length) { return ''; }
		const tmpHead = tmpColumns.map((pColumn) => `<th class="${pColumn.type === 'number' ? 'hor-num' : ''}">${this._esc(pColumn.label)}</th>`).join('');
		const tmpBody = pSlot.rows.map((pRow) =>
		{
			const tmpCells = tmpColumns.map((pColumn) =>
			{
				const tmpValue = pRow ? pRow[pColumn.key] : undefined;
				return `<td class="${pColumn.type === 'number' ? 'hor-num' : ''}">${this._fmtCell(tmpValue)}</td>`;
			}).join('');
			return `<tr>${tmpCells}</tr>`;
		}).join('');
		return `<div class="hor-tablewrap"><table class="hor-table"><thead><tr>${tmpHead}</tr></thead><tbody>${tmpBody}</tbody></table></div>`;
	}

	_fieldsSlotHTML(pSlot)
	{
		const tmpDatum = pSlot.rows[0] || {};
		const tmpColumns = pSlot.columns && pSlot.columns.length ? pSlot.columns : this._columnsFromRows(pSlot.rows);
		const tmpRows = tmpColumns
			.map((pColumn) =>
			{
				const tmpValue = tmpDatum[pColumn.key];
				if (tmpValue == null || tmpValue === '') { return ''; }
				if ((typeof tmpValue === 'string') && this._isLongText(tmpValue))
				{
					return `<div class="hor-textblock"><div class="hor-k">${this._esc(pColumn.label)}</div><div class="hor-textbody">${this._esc(tmpValue)}</div></div>`;
				}
				return `<div class="hor-kv"><span class="hor-k">${this._esc(pColumn.label)}</span><span class="hor-v">${this._fmtValue(tmpValue)}</span></div>`;
			})
			.filter(Boolean)
			.join('');
		return tmpRows ? `<div class="hor-kvlist">${tmpRows}</div>` : '<span class="hor-muted">No structured data.</span>';
	}

	_proseSlotHTML(pSlot)
	{
		return pSlot.texts.map((pText) => `<div class="hor-textbody">${this._esc(pText)}</div>`).join('');
	}

	_mediaSlotHTML(pSlot)
	{
		return pSlot.items.map((pItem) =>
		{
			if (pItem.mediaKind === 'image')
			{
				return `<a class="hor-media-img" href="${this._esc(pItem.url)}" target="_blank" rel="noopener"><img src="${this._esc(pItem.url)}" alt="${this._esc(pItem.fileName)}" loading="lazy" onerror="this.parentNode.classList.add('hor-broken')"/></a>`;
			}
			const tmpIcon = (pItem.mediaKind === 'video') ? 'fa-video' : (pItem.mediaKind === 'audio') ? 'fa-volume-high' : 'fa-file';
			return `<a class="hor-media-file" href="${this._esc(pItem.url)}" target="_blank" rel="noopener"><i class="fas ${tmpIcon}"></i> ${this._esc(pItem.fileName)}</a>`;
		}).join('');
	}

	// ---- Small helpers --------------------------------------------------

	/** Headline datums for compact formats: first structured slot's first row, first N non-empty fields. */
	_headlineFields(pModel, pCount)
	{
		const tmpMax = pCount || 3;
		const tmpOut = [];
		for (let s = 0; s < pModel.slots.length && tmpOut.length < tmpMax; s++)
		{
			const tmpSlot = pModel.slots[s];
			if (tmpSlot.kind === 'prose' && tmpSlot.texts && tmpSlot.texts.length)
			{
				const tmpText = tmpSlot.texts[0];
				tmpOut.push({ label: tmpSlot.prettyName, value: this._esc(tmpText.length > 80 ? `${tmpText.slice(0, 80)}…` : tmpText) });
				continue;
			}
			if ((tmpSlot.kind === 'table' || tmpSlot.kind === 'fields') && tmpSlot.rows && tmpSlot.rows.length)
			{
				const tmpRow = tmpSlot.rows[0] || {};
				const tmpColumns = tmpSlot.columns && tmpSlot.columns.length ? tmpSlot.columns : this._columnsFromRows(tmpSlot.rows);
				for (let c = 0; c < tmpColumns.length && tmpOut.length < tmpMax; c++)
				{
					const tmpValue = tmpRow[tmpColumns[c].key];
					if (tmpValue == null || tmpValue === '' || tmpValue === 0) { continue; }
					tmpOut.push({ label: tmpColumns[c].label, value: this._fmtValue(tmpValue) });
				}
			}
		}
		return tmpOut;
	}

	_columnsFromRows(pRows)
	{
		const tmpSeen = {};
		const tmpColumns = [];
		(pRows || []).forEach((pRow) =>
		{
			Object.keys(pRow || {}).forEach((pKey) =>
			{
				if (tmpSeen[pKey] || this._isNoiseKey(pKey)) { return; }
				tmpSeen[pKey] = true;
				tmpColumns.push({ key: pKey, label: this._prettyKey(pKey), type: (typeof pRow[pKey] === 'number') ? 'number' : 'string' });
			});
		});
		return tmpColumns;
	}

	_metaField(pLabel, pValue)
	{
		return `<div class="hor-meta-field"><div class="hor-meta-label">${this._esc(pLabel)}</div><div class="hor-meta-value">${(pValue == null || pValue === '') ? '<span class="hor-muted">—</span>' : this._esc(pValue)}</div></div>`;
	}

	_labelsHTML(pModel)
	{
		if (!pModel.labels || !pModel.labels.length) { return ''; }
		const tmpRows = pModel.labels
			.map((pLabel) => `<div class="hor-label"><span class="hor-label-t">${this._esc(pLabel.label)}</span>${pLabel.values.map((pValue) => `<span class="hor-chip">${this._esc(pValue)}</span>`).join('')}</div>`)
			.join('');
		return `<div class="hor-section-h">Labels</div><div class="hor-labels">${tmpRows}</div>`;
	}

	_glyphHTML(pType)
	{
		const tmpType = String(pType || '').toLowerCase();
		let tmpIcon = 'fa-clipboard-list';
		if (/image|photo|camera/.test(tmpType)) { tmpIcon = 'fa-image'; }
		else if (/video/.test(tmpType)) { tmpIcon = 'fa-video'; }
		else if (/audio|sound/.test(tmpType)) { tmpIcon = 'fa-volume-high'; }
		else if (/file|document/.test(tmpType)) { tmpIcon = 'fa-file'; }
		else if (/weather|temperature/.test(tmpType)) { tmpIcon = 'fa-cloud-sun'; }
		else if (/equipment|material|goods/.test(tmpType)) { tmpIcon = 'fa-truck'; }
		else if (/safety/.test(tmpType)) { tmpIcon = 'fa-helmet-safety'; }
		else if (/personnel|staff|labor/.test(tmpType)) { tmpIcon = 'fa-users'; }
		else if (/narrative|journal|note/.test(tmpType)) { tmpIcon = 'fa-align-left'; }
		return `<i class="fas ${tmpIcon}"></i>`;
	}

	_initials(pName)
	{
		return String(pName || '').split(/\s+/).filter(Boolean).map((pPart) => pPart[0]).join('').slice(0, 2).toUpperCase();
	}

	_prettyKey(pKey) { return String(pKey || '').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ').replace(/\s+/g, ' ').trim(); }

	_isURL(pValue) { return /^https?:\/\//i.test(String(pValue)); }

	_isLongText(pValue) { const s = String(pValue); return (s.length > 110) || (s.indexOf('\n') >= 0); }

	_isNoiseKey(pKey, pArtifactType)
	{
		if (pKey === 'MultiLine' || pKey === 'URL') { return true; }
		if ((pArtifactType === 'File' || pArtifactType === 'Data_File' || pArtifactType === 'Video' || pArtifactType === 'Audio')
			&& (pKey === 'FileName' || pKey === 'FileMimeType' || pKey === 'AllowedMimeTypes')) { return true; }
		return false;
	}

	_fmtValue(pValue)
	{
		if (typeof pValue === 'boolean') { return pValue ? 'Yes' : 'No'; }
		if (typeof pValue === 'number') { return this._esc(Number.isInteger(pValue) ? pValue.toLocaleString() : pValue.toLocaleString(undefined, { maximumFractionDigits: 4 })); }
		const tmpString = String(pValue);
		if (this._isURL(tmpString)) { return `<a class="hor-link" href="${this._esc(tmpString)}" target="_blank" rel="noopener">${this._esc(tmpString.length > 48 ? `${tmpString.slice(0, 48)}…` : tmpString)}</a>`; }
		return this._esc(tmpString);
	}

	/** Table cell: empty renders as a blank cell (sparse spreadsheet rows are normal). */
	_fmtCell(pValue)
	{
		if (pValue == null || pValue === '') { return ''; }
		return this._fmtValue(pValue);
	}

	_fmtDate(pValue)
	{
		if (!pValue) { return ''; }
		try { const d = new Date(pValue); if (!Number.isNaN(d.getTime())) { return d.toLocaleString(); } } catch (pError) { /* fall through */ }
		return String(pValue).replace('T', ' ').slice(0, 16);
	}

	_fmtDateShort(pValue)
	{
		if (!pValue) { return ''; }
		try { const d = new Date(pValue); if (!Number.isNaN(d.getTime())) { return d.toLocaleDateString(); } } catch (pError) { /* fall through */ }
		return String(pValue).slice(0, 10);
	}

	_esc(pValue)
	{
		if (pValue == null) { return ''; }
		return String(pValue)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	}

	// ---- Styles ---------------------------------------------------------

	/**
	 * Idempotently inject the stylesheet into a document's <head>.  Called
	 * automatically by renderObservationHTML (browser only); safe to call
	 * directly at registration.  Pass a document to target a specific one.
	 */
	injectStyles(pDocument)
	{
		const tmpDoc = pDocument || ((typeof document !== 'undefined') ? document : null);
		if (!tmpDoc || (typeof tmpDoc.getElementById !== 'function')) { return false; }
		if (this._StylesInjected && tmpDoc.getElementById('HeadlightObservationRender-Styles')) { return true; }
		if (tmpDoc.getElementById('HeadlightObservationRender-Styles')) { this._StylesInjected = true; return true; }
		try
		{
			const tmpStyle = tmpDoc.createElement('style');
			tmpStyle.id = 'HeadlightObservationRender-Styles';
			tmpStyle.textContent = this.renderStyles();
			(tmpDoc.head || tmpDoc.getElementsByTagName('head')[0] || tmpDoc.documentElement).appendChild(tmpStyle);
			this._StylesInjected = true;
			return true;
		}
		catch (pError) { return false; }
	}

	/** The stylesheet as a string (for injectStyles, or to inline in a headless render). */
	renderStyles()
	{
		return /*css*/`
.hor { --hor-fg:#1f2733; --hor-muted:#8a94a3; --hor-border:#e6eaf0; --hor-bg:#fff; --hor-soft:#f6f8fb; --hor-accent:#2f6fed; color:var(--hor-fg); font-size:0.9rem; line-height:1.4; box-sizing:border-box; }
.hor *, .hor *::before, .hor *::after { box-sizing:border-box; }
.hor-muted { color:var(--hor-muted); }
.hor-link { color:var(--hor-accent); text-decoration:none; } .hor-link:hover { text-decoration:underline; }
.hor-section-h { font-size:0.7rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--hor-muted); margin:1rem 0 0.5rem; }
.hor-note { color:var(--hor-muted); font-style:italic; padding:0.6rem 0; }
.hor-type-dot { display:inline-block; width:0.5rem; height:0.5rem; border-radius:50%; background:var(--hor-accent); margin-right:0.35rem; vertical-align:middle; }
.hor-chip { display:inline-block; background:var(--hor-soft); border:1px solid var(--hor-border); border-radius:999px; padding:0.05rem 0.5rem; font-size:0.78rem; margin:0 0.25rem 0.25rem 0; }
.hor-glyph { color:var(--hor-muted); display:flex; align-items:center; justify-content:center; }
.hor-glyph i { font-size:1.6em; }
.hor-broken img { display:none; }

/* full */
.hor-full { display:flex; flex-direction:column; }
.hor-hero { text-align:center; margin-bottom:0.9rem; }
.hor-hero-img { max-width:100%; max-height:22rem; border-radius:8px; border:1px solid var(--hor-border); }
.hor-broken.hor-hero::after { content:'no image'; color:var(--hor-muted); font-size:0.8rem; }
.hor-meta { display:grid; grid-template-columns:repeat(auto-fill,minmax(11rem,1fr)); gap:0.6rem; }
.hor-meta-field { min-width:0; }
.hor-meta-label { font-size:0.66rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; color:var(--hor-muted); }
.hor-meta-value { font-size:0.92rem; overflow-wrap:anywhere; }
.hor-desc { margin-top:0.8rem; }
.hor-desc-body, .hor-textbody { white-space:pre-wrap; overflow-wrap:anywhere; background:var(--hor-soft); border:1px solid var(--hor-border); border-radius:8px; padding:0.55rem 0.7rem; }
.hor-slots { display:grid; grid-template-columns:repeat(auto-fill,minmax(16rem,1fr)); gap:0.7rem; align-items:start; }
.hor-slot { border:1px solid var(--hor-border); border-radius:10px; background:var(--hor-bg); overflow:hidden; }
.hor-slot-wide { grid-column:1/-1; }
.hor-slot-h { display:flex; justify-content:space-between; align-items:center; gap:0.5rem; padding:0.45rem 0.7rem; background:var(--hor-soft); border-bottom:1px solid var(--hor-border); font-weight:600; font-size:0.85rem; }
.hor-badge { font-size:0.66rem; font-weight:700; color:var(--hor-muted); background:var(--hor-bg); border:1px solid var(--hor-border); border-radius:6px; padding:0.05rem 0.4rem; white-space:nowrap; }
.hor-slot-b { padding:0.55rem 0.7rem; }
.hor-kvlist { display:flex; flex-direction:column; gap:0.3rem; }
.hor-kv { display:flex; justify-content:space-between; gap:0.75rem; font-size:0.85rem; }
.hor-k { color:var(--hor-muted); } .hor-v { text-align:right; overflow-wrap:anywhere; }
.hor-textblock .hor-k { display:block; margin-bottom:0.25rem; }
.hor-tablewrap { overflow-x:auto; }
.hor-table { border-collapse:collapse; width:100%; font-size:0.82rem; }
.hor-table th, .hor-table td { border-bottom:1px solid var(--hor-border); padding:0.3rem 0.5rem; text-align:left; white-space:nowrap; }
.hor-table thead th { position:sticky; top:0; background:var(--hor-soft); font-size:0.68rem; text-transform:uppercase; letter-spacing:0.03em; color:var(--hor-muted); }
.hor-table td.hor-num, .hor-table th.hor-num { text-align:right; font-variant-numeric:tabular-nums; }
.hor-table tbody tr:hover { background:var(--hor-soft); }
.hor-media-img img { max-width:9rem; max-height:9rem; border-radius:6px; border:1px solid var(--hor-border); margin:0 0.4rem 0.4rem 0; }
.hor-media-file { display:inline-flex; gap:0.4rem; align-items:center; color:var(--hor-accent); text-decoration:none; padding:0.2rem 0; }
.hor-labels { display:flex; flex-direction:column; gap:0.4rem; }
.hor-label-t { font-size:0.68rem; font-weight:700; text-transform:uppercase; letter-spacing:0.03em; color:var(--hor-muted); margin-right:0.5rem; }

/* card */
.hor-card { width:100%; max-width:20rem; border:1px solid var(--hor-border); border-radius:10px; overflow:hidden; background:var(--hor-bg); }
.hor-card-media { height:9rem; background:var(--hor-soft); display:flex; align-items:center; justify-content:center; overflow:hidden; }
.hor-card-media img { width:100%; height:100%; object-fit:cover; }
.hor-card-media.hor-glyph i { font-size:2.4em; }
.hor-card-body { padding:0.55rem 0.7rem; }
.hor-card-title { font-weight:600; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.hor-card-sub { font-size:0.78rem; color:var(--hor-fg); margin:0.15rem 0 0.35rem; }
.hor-card-fields { display:flex; flex-direction:column; gap:0.2rem; }

/* strip (map rail item) */
.hor-strip { display:flex; gap:0.6rem; align-items:flex-start; padding:0.5rem; border:1px solid var(--hor-border); border-radius:9px; background:var(--hor-bg); }
.hor-strip-thumb { flex:0 0 auto; width:3rem; height:3rem; border-radius:6px; object-fit:cover; background:var(--hor-soft); border:1px solid var(--hor-border); }
span.hor-strip-thumb { display:flex; align-items:center; justify-content:center; }
.hor-strip-body { min-width:0; flex:1 1 auto; }
.hor-strip-title { font-weight:600; font-size:0.86rem; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.hor-strip-sub { font-size:0.74rem; color:var(--hor-muted); margin:0.1rem 0; }
.hor-strip-fields { display:flex; flex-wrap:wrap; gap:0.5rem; font-size:0.76rem; }
.hor-strip-kv .hor-k { color:var(--hor-muted); }

/* tooltip */
.hor-tooltip { display:flex; gap:0.6rem; max-width:22rem; padding:0.5rem; }
.hor-tip-media img { width:5rem; height:5rem; object-fit:cover; border-radius:6px; border:1px solid var(--hor-border); }
.hor-tip-body { min-width:0; }
.hor-tip-title { font-weight:600; }
.hor-tip-sub { font-size:0.78rem; }
.hor-tip-meta { font-size:0.74rem; color:var(--hor-muted); margin:0.15rem 0 0.35rem; }
.hor-tip-fields { display:flex; flex-direction:column; gap:0.2rem; font-size:0.8rem; }

/* thumbnail */
.hor-thumbnail { position:relative; width:8rem; height:8rem; border-radius:8px; overflow:hidden; background:var(--hor-soft); border:1px solid var(--hor-border); display:flex; align-items:center; justify-content:center; }
.hor-thumbnail img { width:100%; height:100%; object-fit:cover; }
.hor-thumbnail i { font-size:2.4em; color:var(--hor-muted); }
.hor-thumb-init { position:absolute; font-weight:700; color:var(--hor-muted); }
.hor-thumb-cap { position:absolute; bottom:0; left:0; right:0; background:rgba(0,0,0,0.55); color:#fff; font-size:0.66rem; text-align:center; padding:0.1rem 0; }

/* marker */
.hor-marker { font-size:0.82rem; }
`;
	}
}

module.exports = WebHeadlightObservationRenderService;

module.exports.default_configuration = (
{
	Name:        'HeadlightObservationRender',
	ServiceType: 'HeadlightObservationRender'
});
